import { Col, DatePicker, Input, Row, Table, Tag } from "antd";
import React, { useEffect, useState } from "react";
import axios from "axios";
import { BaseUrl } from "../../../Constants/Constants";
import { Select, Checkbox, Button, notification } from "antd";
import moment from "moment";
import { useNavigate } from "react-router-dom";

const { RangePicker } = DatePicker;

const CoursePartReport = () => {
  const [api, contextHolder] = notification.useNotification();
  const [userData, setUserData] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState("");
  const [teamOption, setTeamOption] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectLoading, setSelectLoading] = useState(false);
  const [allData, setAllData] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [sortbyScore, setSortByScore] = useState("");
  const [betweenDates, setBetweenDates] = useState([]);
  const [clearTableData, setClearTableData] = useState([]);
  const [searchCourse, setSearchCourse] = useState("");
  const [searchEmployee, setSearchEmployee] = useState("");
  const [previousSelectedRowKeys, setPreviousSelectedRowKeys] = useState([]);
  const [filteredTableData, setFilteredTableData] = useState([]);
  const [allSelectedRecords, setAllSelectedRecords] = useState([]);

  const navigate = useNavigate();

  const onSelectChange = (newSelectedRowKeys) => {
    setSelectedRowKeys(newSelectedRowKeys);

    // Get full data for newly selected keys
    const currentlySelected = filteredTableData.filter((item) =>
      newSelectedRowKeys.includes(item.key),
    );

    // Keep previously selected records that are NOT in current table
    const previouslySelected = allSelectedRecords.filter(
      (record) => !filteredTableData.some((item) => item.key === record.key),
    );

    // Merge: previous + current
    setAllSelectedRecords([...previouslySelected, ...currentlySelected]);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  const handleSortChange = (value) => {
    if (value === "score") {
      setSortByScore(true);
    } else {
      setSortByScore(false);
    }
  };

  const now = new Date();
  const padZero = (num) => num.toString().padStart(2, "0");
  const day = padZero(now.getDate());
  const month = padZero(now.getMonth() + 1);
  const year = now.getFullYear().toString().slice(-2);
  const hours = padZero(now.getHours());
  const minutes = padZero(now.getMinutes());
  let formattedCurrentTime = `${day}-${month}-${year}(${hours}.${minutes})`;

  // Updated handleDownload with Employee ID
  const handleDownload = () => {
    const columnNames = [
      "S.No",
      "Employee ID",
      "Username",
      "Email ID",
      "Course",
      "File Type",
      "Time Spent",
      "Status",
      "Completion Date",
    ];

    const rows = userData.map((row, index) => {
      const employeeId = row.user?.employee_id || "-";
      const username = row.user?.username || "";
      const email = row.user?.email || "";
      const courseTitle = row.course?.course_title || "";
      const fileType = row.userSession?.training_module_type || "N/A";
      const timeSpent = row.total_time === "00:00:00" ? "-" : row.total_time;
      const status = row.isChecked ? "Completed" : "No";
      const completionDate = row.completionDateTime
        ? moment(row.completionDateTime).format("DD-MM-YYYY HH:mm:ss")
        : "-";

      return [
        index + 1,
        employeeId,
        username,
        email,
        courseTitle,
        fileType,
        timeSpent,
        status,
        completionDate,
      ];
    });

    const csvContent = `data:text/csv;charset=utf-8,${[
      columnNames.join(","),
      ...rows.map((r) => r.join(",")),
    ].join("\n")}`;

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute(
      "download",
      `Course_Completion_Report_${formattedCurrentTime}.csv`,
    );
    document.body.appendChild(link);
    link.click();
  };

  const filterData = () => {
    let tempFilteredData = [...allData];

    if (searchEmployee) {
      tempFilteredData = tempFilteredData.filter((data) =>
        data.user?.username
          ?.toLowerCase()
          .includes(searchEmployee.toLowerCase()),
      );
    }

    // Course filter:
    if (searchCourse) {
      tempFilteredData = tempFilteredData.filter((data) =>
        data.course?.course_title
          ?.toLowerCase()
          .includes(searchCourse.toLowerCase()),
      );
    }

    // Map with keys for row selection:
    tempFilteredData = tempFilteredData.map((item, index) => ({
      ...item,
      key: item.user_part_tracker_id || index + 1, // Use unique ID or index
    }));
    // Restore checkboxes for previously selected items
    const keysInCurrentView = tempFilteredData.map((item) => item.key);
    const selectedKeysStillVisible = allSelectedRecords
      .filter((record) => keysInCurrentView.includes(record.key))
      .map((record) => record.key);

    setSelectedRowKeys(selectedKeysStillVisible);

    setFilteredTableData(tempFilteredData);
  };

  useEffect(() => {
    if (allSelectedRecords.length > 0) {
      // Use allSelectedRecords directly
      setUserData(allSelectedRecords);
    } else {
      // No selections - use all current table data
      setUserData(filteredTableData);
    }
  }, [allSelectedRecords, filteredTableData]);

  useEffect(() => {
    filterData();
  }, [searchEmployee, searchCourse, allData]);

  // clears selections when filters change
  // useEffect(() => {
  //   setSelectedRowKeys([]);
  // }, [searchEmployee, searchCourse, betweenDates]);

  // Fetch data with time spent
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const res = await axios.get(
          `${BaseUrl}user-course-part-tracker/completed-with-time`,
          { withCredentials: true },
        );

        if (Array.isArray(res.data)) {
          setAllData(res.data);
          setFilteredTableData(res.data);
          setUserData(res.data);
        } else {
          console.error("API response is not an array:", res.data);
          setAllData([]);
          setFilteredTableData([]);
        }
      } catch (err) {
        console.error("Error fetching data:", err);
        setAllData([]);
        setFilteredTableData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Updated columns with Employee ID and renamed Course Type to File Type
  const columns = [
    {
      title: "S.No",
      key: "index",
      render: (text, record, index) => index + 1,
    },
    {
      title: "Employee ID",
      dataIndex: ["user", "employee_id"],
      key: "employee_id",
      render: (text) => text || "-",
    },
    {
      title: "Name",
      dataIndex: ["user", "username"],
      key: "username",
    },
    {
      title: "Email",
      dataIndex: ["user", "email"],
      key: "email",
    },
    {
      title: "Course",
      dataIndex: ["course", "course_title"],
      key: "course_title",
    },
    {
      title: "File Type",
      dataIndex: ["course_type", "course_type"],
      key: "course_type",
      render: (_, record) => (
        <span>{record.userSession?.training_module_type || "N/A"}</span>
      ),
    },
    {
      title: "Time Spent",
      dataIndex: "total_time",
      key: "total_time",
      render: (text) => {
        // Show "-" if time is 0 or "00:00:00"
        if (!text || text === "00:00:00" || text === "0") {
          return <span>-</span>;
        }
        return <span>{text}</span>;
      },
    },
    {
      title: "Status",
      dataIndex: "isChecked",
      key: "isChecked",
      render: (value) => (value ? "Completed" : "No"),
    },
    {
      title: "Completion Date",
      dataIndex: "completionDateTime",
      key: "completionDateTime",
      align: "center",
      render: (text) => {
        const date = moment(text);
        return (
          <p>{date.isValid() ? date.format("DD-MM-YYYY HH:mm:ss") : "N/A"}</p>
        );
      },
      sorter: (a, b) => {
        const dateA = moment(a.completionDateTime);
        const dateB = moment(b.completionDateTime);

        if (!dateA.isValid()) return 1;
        if (!dateB.isValid()) return -1;

        return dateA - dateB;
      },
    },
  ];

  useEffect(() => {
    axios
      .get(`${BaseUrl}teams`, { withCredentials: true })
      .then((res) => {
        let teams = res.data.map((item) => {
          let teamsObj = {
            value: item.team_id,
            label: item.Team_name,
          };
          return teamsObj;
        });
        setTeamOption(teams);
      })
      .catch((err) => {
        console.log(err.message);
      });
  }, []);

  const getDatesBetween = (startDate, endDate) => {
    let dates = [];
    let currentDate = moment(startDate);
    const end = moment(endDate);

    while (currentDate <= end) {
      dates.push(currentDate.format("YYYY-MM-DD"));
      currentDate = currentDate.add(1, "days");
    }

    return dates;
  };
  const handleDateRangeChange = (dates, dateString) => {
    const [startDate, endDate] = dateString;
    const datesBetween = getDatesBetween(startDate, endDate);

    console.log(datesBetween, "dateString");
    setBetweenDates(datesBetween);
  };

  const handleSelectedRowToDownload = () => {
    const allCurrentKeys = filteredTableData.map((row) => row.key);

    if (
      selectedRowKeys.length === filteredTableData.length &&
      filteredTableData.length > 0
    ) {
      // Deselect all in current view
      const keysToRemove = new Set(allCurrentKeys);
      const remaining = allSelectedRecords.filter(
        (r) => !keysToRemove.has(r.key),
      );
      setAllSelectedRecords(remaining);
      setSelectedRowKeys([]);
    } else {
      // Select all in current view
      const previousRecords = allSelectedRecords.filter(
        (r) => !filteredTableData.some((item) => item.key === r.key),
      );
      setAllSelectedRecords([...previousRecords, ...filteredTableData]);
      setSelectedRowKeys(allCurrentKeys);
    }
  };
  const openNotificationWithIconWarning = (type) => {
    api["warning"]({
      message: "Warning!!!",
      description: type,
    });
  };

  const handleRoute = (e) => {
    navigate(e);
  };

  return (
    <div className="scoresMainDiv">
      <div className="reportsSideHeading">
        <h4
          className="nonActiveHeading"
          onClick={() => handleRoute("/coursesactivityreports")}
        >
          Course Activity Report
        </h4>
        <h4
          className="nonActiveHeading"
          onClick={() => handleRoute("/trainingreports")}
        >
          Training Report
        </h4>
        <h4
          className="activeHeading"
          onClick={() => handleRoute("/courseParts")}
        >
          Course part Report
        </h4>
        <h4
          className="nonActiveHeading"
          onClick={() => handleRoute("/reports")}
        >
          Course Report
        </h4>
      </div>
      {contextHolder}
      <h2>Courses Completion Report</h2>
      <div>
        <Row
          gutter={12}
          align="middle"
          justify="space-between"
          style={{ marginBottom: 10 }}
        >
          {/* Left side */}
          <Col>
            <RangePicker
              style={{ height: 36 }}
              onChange={handleDateRangeChange}
            />
          </Col>

          <Col>
            <Input
              placeholder="Search by Course"
              style={{ height: 36, width: 200 }}
              onChange={(e) => setSearchCourse(e.target.value)}
            />
          </Col>

          <Col>
            <Input
              placeholder="Search by Employee Name"
              style={{ height: 36, width: 200 }}
              onChange={(e) => setSearchEmployee(e.target.value)}
            />
          </Col>

          {/* Right side */}
          <Col>
            <Button
              onClick={handleSelectedRowToDownload}
              disabled={loading || filteredTableData.length === 0}
              style={{ height: 36 }}
            >
              {loading
                ? "Loading..."
                : selectedRowKeys.length === filteredTableData.length &&
                    filteredTableData.length > 0
                  ? `Deselect All (${filteredTableData.length})`
                  : `Select All (${filteredTableData.length})`}
            </Button>
          </Col>

          <Col>
            <Button
              type="primary"
              style={{ height: 36 }}
              onClick={handleDownload}
            >
              Download Report
            </Button>
          </Col>
        </Row>
        {/* This row shows the clarity on selected & persisted data */}
        <Row
          gutter={12}
          align="left"
          justify="start"
          style={{ marginBottom: 10 }}
        >
          {allSelectedRecords.length > 0 && (
            <Col>
              <Button
                danger
                onClick={() => {
                  setSelectedRowKeys([]);
                  setAllSelectedRecords([]);
                }}
              >
                Clear All ({allSelectedRecords.length})
              </Button>
            </Col>
          )}
          {selectedRowKeys.length > 0 && (
            <Col>
              <Tag color="blue">
                {selectedRowKeys.length} selected
                {allSelectedRecords.length !== selectedRowKeys.length &&
                  ` (${allSelectedRecords.length} total)`}
              </Tag>
            </Col>
          )}
        </Row>
        <div className="tabletopbutton"></div>

        <Table
          columns={columns}
          dataSource={filteredTableData}
          loading={loading}
          pagination={{
            position: ["bottomCenter"],
            showSizeChanger: false,
          }}
          rowKey="user_part_tracker_id"
          rowSelection={rowSelection}
        />
      </div>
    </div>
  );
};

export default CoursePartReport;
